import { useState, useMemo, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  useCreateKeywordTheme,
  useDeleteKeywordTheme,
  useGenerateThemeSuggestions,
  useAcceptThemeSuggestion,
  useGenerateKeywordSuggestions,
  useAddKeywordToTheme,
  keywordThemesQueryKey,
} from "@/resources/keyword-theme";
import type { ThemeSuggestion, KeywordSuggestion } from "@/resources/keyword-theme";
import type { KeywordTheme } from "@/types/keyword-theme";
import type { Keyword } from "@/types/keyword";
import { toggleSetItem } from "./KeywordThemeShared";
import { AiSuggestionsTab } from "./AiSuggestionsTab";
import { ManualThemeTab } from "./ManualThemeTab";
import { searchKeywords } from "@/utils/keywordSearch";
import { toast } from "@/hooks/use-toast";
import queryClient from "@/query-client";

interface AddThemeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productId: number;
  allKeywords: Keyword[];
  existingThemesCount?: number;
  initialKeywordId?: number | null;
  /**
   * AUT-201: keywords already selected in the Keyword Bank when "Create new
   * theme…" is chosen from Bulk Actions. Without this the modal opened empty
   * and the selection was silently dropped, creating a theme with no keywords.
   */
  initialKeywordIds?: number[];
  /**
   * Spec 5a: while under 3 themes, Signal AI suggestions are fetched
   * automatically (KeywordBankContentV2's useAutoThemeSuggestions, on page
   * load) rather than waiting for a person to click "Ask Signal AI" here.
   * undefined = not applicable (readOnly, or already at 3+ themes) or still
   * loading with nothing cached yet; [] = fetched, genuinely nothing found;
   * [...] = fetched, ready to show immediately on open.
   */
  prefetchedSuggestions?: ThemeSuggestion[];
  isPrefetchingSuggestions?: boolean;
}

export function AddThemeModal({
  open,
  onOpenChange,
  productId,
  allKeywords,
  existingThemesCount = 0,
  initialKeywordId,
  initialKeywordIds,
  prefetchedSuggestions,
  isPrefetchingSuggestions,
}: AddThemeModalProps) {
  const [tab, setTab] = useState<"ai" | "manual">("ai");

  // ─── AI tab ────────────────────────────────────────────────────────
  // null = not tried yet; [] = tried but no results; [...] = has results
  const [aiSuggestions, setAiSuggestions] = useState<ThemeSuggestion[] | null>(null);
  const [acceptingIndex, setAcceptingIndex] = useState<number | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // Accepted suggestions stay visible (in a disabled "Added" state) instead
  // of being removed from aiSuggestions -- removing them made the modal fall
  // through to the "No new theme opportunities found" empty state right
  // after a successful accept, which reads as an error. Keyed by theme_name
  // since dismissing a card (a separate, real removal) shifts indices.
  const [acceptedNames, setAcceptedNames] = useState<Set<string>>(new Set());

  // ─── Manual tab ────────────────────────────────────────────────────
  // Phase 1: typing a name
  const [name, setName] = useState("");
  const [showNameDropdown, setShowNameDropdown] = useState(false);

  // Phase 2: name staged, picking keywords
  const [stagedName, setStagedName] = useState<string | null>(null);
  // Set when the staged name is (or exactly matches) an existing keyword-bank
  // entry — that keyword becomes the head term immediately (spec 5b scenario A).
  const [stagedHeadTermId, setStagedHeadTermId] = useState<number | null>(null);
  const [kwSearch, setKwSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [aiKwSuggestions, setAiKwSuggestions] = useState<KeywordSuggestion[] | null>(null);
  // theme may be created early (when Ask Signal AI is clicked) to get a themeId
  const [earlyCreatedThemeId, setEarlyCreatedThemeId] = useState<number | null>(null);
  const [isAskingAI, setIsAskingAI] = useState(false);

  const nameInputRef = useRef<HTMLInputElement>(null);

  const { mutateAsync: createTheme } = useCreateKeywordTheme();
  const { mutateAsync: deleteTheme } = useDeleteKeywordTheme();
  const { mutateAsync: generateSuggestions, isPending: isGenerating } = useGenerateThemeSuggestions();
  const { mutateAsync: acceptSuggestion } = useAcceptThemeSuggestion();
  const { mutateAsync: generateKwSuggestions } = useGenerateKeywordSuggestions();
  const { mutateAsync: addKeyword } = useAddKeywordToTheme();

  const autoSuggest = existingThemesCount < 3;
  // Loading covers both the click-triggered Refresh/"Ask Signal AI" mutation
  // and the initial automatic fetch (while under 3 themes) before it's had
  // a chance to populate aiSuggestions yet.
  const isLoadingAiSuggestions =
    isGenerating || (autoSuggest && !!isPrefetchingSuggestions && aiSuggestions === null);

  useEffect(() => {
    if (open && initialKeywordIds?.length) {
      // AUT-204: land on "Add your own" too -- seeding the selection is useless
      // if the modal opens on the Signal AI tab, where the user can't see it.
      setTab("manual");
      setSelectedIds(new Set(initialKeywordIds));
      return;
    }
    if (open && initialKeywordId) {
      setTab("manual");
      setSelectedIds(new Set([initialKeywordId]));
    }
  }, [open, initialKeywordId, initialKeywordIds]);

  // Show the auto-fetched suggestions the moment the modal opens, if
  // they're already in -- only while aiSuggestions is still null, so this
  // never clobbers a suggestion set the person already got from Refresh or
  // from opening the modal after 3+ themes (autoSuggest false, nothing
  // prefetched at all in that case).
  useEffect(() => {
    if (open && autoSuggest && aiSuggestions === null && prefetchedSuggestions !== undefined) {
      setAiSuggestions(prefetchedSuggestions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, autoSuggest, prefetchedSuggestions]);

  const reset = () => {
    setTab("ai");
    setAiSuggestions(null);
    setAcceptingIndex(null);
    setExpanded(new Set());
    setAcceptedNames(new Set());
    setName("");
    setShowNameDropdown(false);
    setStagedName(null);
    setStagedHeadTermId(null);
    setKwSearch("");
    setSelectedIds(new Set());
    setAiKwSuggestions(null);
    setEarlyCreatedThemeId(null);
    setIsAskingAI(false);
  };

  // ─── Name autocomplete ─────────────────────────────────────────────
  const keywordById = useMemo(
    () => new Map(allKeywords.map((k) => [k.id, k])),
    [allKeywords],
  );

  const nameSuggestions = useMemo(
    () => searchKeywords(allKeywords, name, 8),
    [name, allKeywords],
  );

  // ─── Keyword bank search ───────────────────────────────────────────
  const kwResults = useMemo(
    () => searchKeywords(allKeywords, kwSearch, 20),
    [kwSearch, allKeywords],
  );

  const selectedKeywords = useMemo(
    () => allKeywords.filter((k) => selectedIds.has(k.id)),
    [allKeywords, selectedIds],
  );

  const toggleId = (id: number) => setSelectedIds((prev) => toggleSetItem(prev, id));

  // ─── AI tab handlers ───────────────────────────────────────────────
  const handleAskAI = async () => {
    try {
      const result = await generateSuggestions({ productId });
      setAiSuggestions(result.suggestions ?? []);
      setAcceptedNames(new Set());
    } catch {
      // handleApiError already showed a toast; keep aiSuggestions null so user sees the start screen
    }
  };

  const handleDismissSuggestion = (index: number) => {
    setAiSuggestions((prev) => (prev ?? []).filter((_, j) => j !== index));
  };

  const handleToggleExpand = (themeName: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(themeName)) next.delete(themeName); else next.add(themeName);
      return next;
    });
  };

  const handleAcceptSuggestion = async (suggestion: ThemeSuggestion, index: number) => {
    setAcceptingIndex(index);

    const queryKey = keywordThemesQueryKey(productId);
    const previous = queryClient.getQueryData<KeywordTheme[]>(queryKey);
    const headTermId = suggestion.head_term_id;
    const optimisticTheme: KeywordTheme = {
      id: -Date.now(),
      product_id: productId,
      name: suggestion.theme_name,
      ai_summary: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      keywords: (suggestion.keywords ?? []).map((kw) => ({
        id: 0,
        keyword_id: kw.keyword_id,
        is_head_term: kw.keyword_id === headTermId,
        search: kw.keyword,
        search_volume: kw.search_volume,
        kps: kw.kps,
        kps_raw: null,
        rank: kw.hero_rank,
        hero_relevance: kw.relevance,
        search_volume_trend: null,
      })),
    };
    queryClient.setQueryData<KeywordTheme[]>(queryKey, (old) => [...(old ?? []), optimisticTheme]);

    try {
      const allKeywordIds = (suggestion.keywords ?? []).map((k) => k.keyword_id);
      if (suggestion.head_term_id && !allKeywordIds.includes(suggestion.head_term_id)) {
        allKeywordIds.unshift(suggestion.head_term_id);
      }
      await acceptSuggestion({
        productId,
        name: suggestion.theme_name,
        keyword_ids: allKeywordIds,
        head_term_keyword_id: suggestion.head_term_id || undefined,
      });
      setAcceptedNames((prev) => new Set(prev).add(suggestion.theme_name));
      setExpanded((prev) => {
        const next = new Set(prev);
        next.delete(suggestion.theme_name);
        return next;
      });
      toast({
        title: "Theme added",
        description: `"${suggestion.theme_name}" added with ${(suggestion.keywords ?? []).length} keyword${(suggestion.keywords ?? []).length === 1 ? "" : "s"}.`,
      });
    } catch {
      // acceptSuggestion's onError (handleApiError) already shows a toast.
      queryClient.setQueryData(queryKey, previous);
    } finally {
      setAcceptingIndex(null);
    }
  };

  // ─── Manual tab handlers ───────────────────────────────────────────
  const handleStageTheme = (value?: string, keywordId?: number) => {
    const trimmed = (value ?? name).trim();
    if (!trimmed) return;
    // Scenario A (spec 5b): the staged name is an existing bank keyword —
    // either explicitly picked from the autocomplete dropdown, or typed out
    // in full. That keyword becomes the head term immediately.
    const matchId =
      keywordId ??
      allKeywords.find((k) => k.search.toLowerCase() === trimmed.toLowerCase())?.id ??
      null;
    setStagedName(trimmed);
    setStagedHeadTermId(matchId);
    // AUT-204: add the head term to whatever is already selected rather than
    // replacing it -- when the modal was opened from Bulk Actions the selection
    // is pre-seeded, and picking a theme name that happens to match a bank
    // keyword used to wipe it, leaving only that one keyword.
    if (matchId) setSelectedIds((prev) => new Set(prev).add(matchId));
    setName("");
    setShowNameDropdown(false);
  };

  const handleUnstageTheme = () => {
    setName(stagedName ?? "");
    setStagedName(null);
    setStagedHeadTermId(null);
    // Back to naming, not "discard my keywords": restore the bulk pre-selection
    // the modal was opened with, otherwise unstaging loses it irrecoverably.
    setSelectedIds(new Set(initialKeywordIds ?? []));
    setAiKwSuggestions(null);
    setEarlyCreatedThemeId(null);
    setKwSearch("");
  };

  const handleAskAIKeywords = async () => {
    if (!stagedName) return;
    setIsAskingAI(true);
    try {
      // Create the theme now so we have a themeId for the suggestions API
      let themeId = earlyCreatedThemeId;
      if (!themeId) {
        const created = await createTheme({ productId, name: stagedName });
        themeId = created.id;
        setEarlyCreatedThemeId(created.id);
        // Lock in the picked head term before asking AI, so the prompt
        // reflects scenario A ("build the rest around it") instead of
        // scenario B (AI infers its own head term from the name).
        if (stagedHeadTermId) {
          await addKeyword({ productId, themeId, keywordId: stagedHeadTermId, is_head_term: true });
        }
      }
      const result = await generateKwSuggestions({ productId, themeId });
      const suggestions = result.keywords ?? [];
      setAiKwSuggestions(suggestions);
      // Pre-select all suggestions
      setSelectedIds((prev) => {
        const next = new Set(prev);
        suggestions.forEach((s) => next.add(s.keyword_id));
        return next;
      });
    } finally {
      setIsAskingAI(false);
    }
  };

  const handleAddAllSuggestions = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      (aiKwSuggestions ?? []).forEach((s) => next.add(s.keyword_id));
      return next;
    });
  };

  const handleSaveTheme = async () => {
    if (!stagedName) return;

    // Capture before reset() clears state
    const themeName = stagedName;
    const themeId0 = earlyCreatedThemeId;
    const kwIds = Array.from(selectedIds);
    const kwsSnap = [...selectedKeywords];

    // Optimistic update: show theme with keywords immediately
    const queryKey = keywordThemesQueryKey(productId);
    const previous = queryClient.getQueryData<KeywordTheme[]>(queryKey);
    // The explicitly staged head term (scenario A) wins; otherwise fall back
    // to highest search volume, matching spec's "usually highest search volume" default.
    const headTermId =
      stagedHeadTermId != null && kwIds.includes(stagedHeadTermId)
        ? stagedHeadTermId
        : kwsSnap.length > 0
          ? kwsSnap.reduce((best, kw) => (kw.search_volume ?? 0) > (best.search_volume ?? 0) ? kw : best).id
          : null;
    const optimisticTheme: KeywordTheme = {
      id: -Date.now(),
      product_id: productId,
      name: themeName,
      ai_summary: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      keywords: (kwsSnap as typeof allKeywords).map((kw) => ({
        id: 0,
        keyword_id: kw.id,
        is_head_term: kw.id === headTermId,
        search: kw.search,
        search_volume: kw.search_volume ?? null,
        kps: kw.kps ?? null,
        kps_raw: null,
        rank: kw.rank ?? null,
        hero_relevance: kw.hero_relevance ?? null,
        search_volume_trend: kw.search_volume_trend ?? null,
      })),
    };
    queryClient.setQueryData<KeywordTheme[]>(queryKey, (old) => [...(old ?? []), optimisticTheme]);

    // Close modal immediately — API calls run in background
    reset();
    onOpenChange(false);

    try {
      let themeId = themeId0;
      if (!themeId) {
        const created = await createTheme({ productId, name: themeName });
        themeId = created.id;
      }
      if (kwIds.length > 0) {
        // is_head_term is passed explicitly (rather than relying on the
        // backend's "first keyword added to an empty theme" auto-assignment)
        // because these calls run in parallel — without an explicit flag,
        // whichever request lands first would win the head term arbitrarily,
        // and re-adding an already-assigned head term without the flag would
        // silently unset it.
        await Promise.all(
          kwIds.map((id) =>
            addKeyword({ productId, themeId: themeId!, keywordId: id, is_head_term: id === headTermId }),
          ),
        );
        // addKeyword.onSuccess already invalidates → real data replaces the optimistic entry
      } else {
        queryClient.invalidateQueries({ queryKey });
      }
      toast({
        title: "Theme added",
        description: `"${themeName}" added with ${kwIds.length} keyword${kwIds.length === 1 ? "" : "s"}.`,
      });
    } catch {
      // createTheme/addKeyword's onError (handleApiError) already shows a toast.
      queryClient.setQueryData(queryKey, previous);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) {
          const orphanId = earlyCreatedThemeId;
          reset();
          if (orphanId) {
            // Theme was created early for AI keyword suggestions but never saved — delete it
            deleteTheme({ productId, themeId: orphanId }).catch(() => {
              // Best-effort: if delete fails, invalidate so the orphan is at least visible
              queryClient.invalidateQueries({ queryKey: keywordThemesQueryKey(productId) });
            });
          }
        }
        onOpenChange(v);
      }}
    >
      <DialogContent className="max-w-4xl p-0 gap-0 flex flex-col max-h-[85vh] overflow-hidden">
        <DialogHeader className="px-6 pt-5 pb-0 shrink-0">
          <DialogTitle>Add a theme</DialogTitle>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex border-b px-6 mt-3 shrink-0">
          {(["ai", "manual"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={cn(
                "pb-2 px-1 mr-4 text-sm font-medium border-b-2 -mb-px transition-colors",
                tab === t
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {t === "ai" ? "✦ Signal AI recommendations" : "+ Add your own"}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto min-h-0 px-6 py-5">
          {tab === "ai" ? (
            <AiSuggestionsTab
              isLoadingAiSuggestions={isLoadingAiSuggestions}
              aiSuggestions={aiSuggestions}
              autoSuggest={autoSuggest}
              isGenerating={isGenerating}
              acceptingIndex={acceptingIndex}
              expanded={expanded}
              acceptedNames={acceptedNames}
              onAskAI={handleAskAI}
              onDismiss={handleDismissSuggestion}
              onToggleExpand={handleToggleExpand}
              onAcceptSuggestion={handleAcceptSuggestion}
              onSwitchToManual={() => setTab("manual")}
            />
          ) : (
            <ManualThemeTab
              name={name}
              onNameChange={setName}
              showNameDropdown={showNameDropdown}
              onShowNameDropdownChange={setShowNameDropdown}
              nameSuggestions={nameSuggestions}
              stagedName={stagedName}
              onStage={handleStageTheme}
              onUnstage={handleUnstageTheme}
              nameInputRef={nameInputRef}
              selectedKeywords={selectedKeywords}
              selectedIds={selectedIds}
              onToggleId={toggleId}
              kwSearch={kwSearch}
              onKwSearchChange={setKwSearch}
              kwResults={kwResults}
              isAskingAI={isAskingAI}
              aiKwSuggestions={aiKwSuggestions}
              keywordById={keywordById}
              onAskAIKeywords={handleAskAIKeywords}
              onAddAllSuggestions={handleAddAllSuggestions}
            />
          )}
        </div>

        {/* Footer */}
        <div className="border-t px-6 py-3 flex justify-end gap-2 shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {tab === "ai" ? "Done" : "Cancel"}
          </Button>
          {tab === "manual" && (
            <Button onClick={handleSaveTheme} disabled={!stagedName}>
              Save theme
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
